/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Resource } from '@ngxp/rest';
import { catchError, first, map, Observable, of, startWith, switchMap, tap, throwError } from 'rxjs';
import { mapToPatch } from '../data-rest/data-rest.util';
import { isUnprocessableEntity } from '../http.util';
import { StateService } from '../ngrx/state.service';
import { HttpError } from '../tech.model';
import { LinkRelationName, ResourceServiceConfig } from './resource.model';
import { ResourceRepository } from './resource.repository';
import { mapToResource } from './resource.rxjs.operator';
import { ResourceService } from './resource.service';
import { createEmptyStateResource, createErrorStateResource, createStateResource, StateResource } from './resource.util';

@Injectable()
export class ApiResourceService<B extends Resource, T extends Resource> extends ResourceService<B, T> {
  private readonly repository: ResourceRepository = inject(ResourceRepository);

  constructor(
    protected config: ResourceServiceConfig<B>,
    /**
     * @deprecated Der Parameter wird nicht mehr gebraucht.
     */
    protected oldStateService?: StateService,
    /**
     * @deprecated Der Parameter wird nicht mehr gebraucht.
     */
    protected oldResourceRepository?: ResourceRepository,
  ) {
    super(config);
  }

  public save(toSave: unknown): Observable<StateResource<T>> {
    return this._executeSafely((resource: T) => this._doSave(resource, toSave).pipe(tap(() => this.reloadResource())));
  }

  public patch(toPatch: unknown): Observable<StateResource<T>> {
    return this._executeSafely((resource: T) =>
      this._doPatch(resource, mapToPatch(toPatch)).pipe(tap(() => this.reloadResource())),
    );
  }

  public mergePatch(toPatch: unknown): Observable<StateResource<T>> {
    return this._executeSafely((resource: T) => this._doMergePatch(resource, toPatch).pipe(tap(() => this.reloadResource())));
  }

  public delete(): Observable<StateResource<T>> {
    return this._executeSafely((resource: T) => this._doDelete(resource));
  }

  public executeAction<P, R extends Resource = T>(linkRel: LinkRelationName, payload?: P): Observable<StateResource<R>> {
    return this._executeSafely<R>((resource: T) => this._doExecuteAction<P, R>(resource, linkRel, payload));
  }

  _executeSafely<R extends Resource = T>(action: (resource: T) => Observable<R>): Observable<StateResource<R>> {
    return this.selectResource().pipe(
      mapToResource<T>(),
      first(),
      switchMap((resource: T) =>
        action(resource).pipe(
          map((value: R) => createStateResource<R>(value)),
          catchError((errorResponse: HttpErrorResponse) => this._handleError<R>(errorResponse)),
        ),
      ),
      startWith(createEmptyStateResource<R>(true)),
    );
  }

  _doSave(resource: T, toSave: unknown): Observable<T> {
    return this.repository.save<T>({
      resource,
      linkRel: this.config.edit.linkRel,
      toSave,
    });
  }

  _doPatch(resource: T, toPatch: unknown): Observable<T> {
    return this.repository.patch<T>({
      resource,
      linkRel: this.config.edit.linkRel,
      toSave: toPatch,
    });
  }

  _doMergePatch(resource: T, toPatch: unknown): Observable<T> {
    return this.repository.mergePatch<T>({
      resource,
      linkRel: this.config.edit.linkRel,
      toSave: toPatch,
    });
  }

  _doExecuteAction<P, R extends Resource = T>(resource: T, linkRel: LinkRelationName, payload?: P): Observable<R> {
    return this.repository.createResource<R>({ resource, linkRel, toCreate: payload ?? {} });
  }

  _doDelete(resource: T): Observable<T> {
    return this.repository.delete(resource, this.config.delete.linkRel);
  }

  _handleError<R extends Resource = T>(errorResponse: HttpErrorResponse): Observable<StateResource<R>> {
    if (isUnprocessableEntity(errorResponse.status)) {
      // @ngxp/rest (save/create/delete) liefert ProblemDetail direkt als errorResponse,
      // Angular HttpClient (patch/mergePatch) kapselt es in errorResponse.error.
      const problemDetail = (errorResponse.error ?? errorResponse) as HttpError;
      return of(createErrorStateResource(problemDetail));
    }
    return throwError(() => errorResponse);
  }
}
